import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { Form } from "../../components/Form";
import { Input } from "../../components/Input";
import { Toggle } from "../../components/Toggle";
import { spacing, borderRadius } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import type {
  RestCreateModifierGroup,
  RestModifierGroup,
} from "@KassaroAB/shared/types/modifierGroup";
import { FormSection } from "../../components/FormSection";
import { useCatalogScope } from "../../hooks/useCatalogScope";
import { useGetModifiersQuery } from "../../query/modifiersQuery";
import { CATALOG_NEW_ITEM_ID } from "../catalog/catalogNav";

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  font-size: 13px;
  cursor: pointer;
`;

const InlineLink = styled(Link)`
  color: ${colors.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyHint = styled.p`
  margin: 0;
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  line-height: 1.45;
  color: ${colors.textMuted};
  background-color: ${colors.background};
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
`;

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
`;

interface ModifierGroupFormProps {
  formId: string;
  group?: RestModifierGroup;
  savedTick?: number;
  onSubmit: (data: RestCreateModifierGroup) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

const parseOptionalInt = (value: string) => {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.floor(parsed) : null;
};

export const ModifierGroupForm = ({
  formId,
  group,
  savedTick,
  onSubmit,
  onDirtyChange,
}: ModifierGroupFormProps) => {
  const { t } = useTranslation();
  const { scope, catalogPath } = useCatalogScope();
  const { data: modifiers } = useGetModifiersQuery(scope);
  const modifiersPath = catalogPath("modifiers", CATALOG_NEW_ITEM_ID);

  const form = useForm({
    defaultValues: {
      name: group?.name ?? "",
      modifiers: group?.modifiers ?? [],
      minAmount:
        group?.minAmount !== null && group?.minAmount !== undefined
          ? String(group.minAmount)
          : "",
      maxAmount:
        group?.maxAmount !== null && group?.maxAmount !== undefined
          ? String(group.maxAmount)
          : "",
      alwaysPrompt: group?.alwaysPrompt ?? false,
    },
    validators: {
      onSubmit: ({ value }) => {
        const minAmount = parseOptionalInt(value.minAmount);
        const maxAmount = parseOptionalInt(value.maxAmount);
        if (
          minAmount !== null &&
          maxAmount !== null &&
          minAmount > maxAmount
        ) {
          const message = t("modifierGroupForm.minExceedsMax");
          return {
            fields: {
              minAmount: message,
              maxAmount: message,
            },
          };
        }
        return undefined;
      },
    },
    onSubmit: ({ value }) => {
      onSubmit({
        name: value.name,
        modifiers: value.modifiers,
        minAmount: parseOptionalInt(value.minAmount),
        maxAmount: parseOptionalInt(value.maxAmount),
        alwaysPrompt: value.alwaysPrompt,
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!savedTick) return;
    form.reset(form.state.values);
  }, [savedTick, form]);

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <FormSection title={t("modifierGroupForm.basicInformation")}>
        <Fields>
          <form.Field name="name">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("modifierGroupForm.name")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="minAmount">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("modifierGroupForm.minAmount")}</FormLabel>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t("productForm.notSet")}
                />
                {field.state.meta.errors.length > 0 && (
                  <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
                )}
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="maxAmount">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("modifierGroupForm.maxAmount")}</FormLabel>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t("productForm.notSet")}
                />
                {field.state.meta.errors.length > 0 && (
                  <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
                )}
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="alwaysPrompt">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("modifierGroupForm.alwaysPrompt")}</FormLabel>
                <Toggle
                  checked={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
        </Fields>
      </FormSection>

      <FormSection title={t("modifierGroupForm.modifiers")}>
        <form.Field name="modifiers">
          {(field) =>
            (modifiers?.length ?? 0) > 0 ? (
              <FormGroup>
                <CheckboxList>
                  {modifiers?.map((modifier) => (
                    <CheckboxLabel key={modifier.id}>
                      <input
                        type="checkbox"
                        checked={field.state.value.includes(modifier.id)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...field.state.value, modifier.id]
                            : field.state.value.filter(
                                (id) => id !== modifier.id,
                              );
                          field.handleChange(next);
                        }}
                      />
                      {modifier.name}
                    </CheckboxLabel>
                  ))}
                </CheckboxList>
              </FormGroup>
            ) : (
              <EmptyHint>
                {t("modifierGroupForm.noModifiers")}{" "}
                <InlineLink to={modifiersPath}>
                  {t("modifierGroupForm.createModifier")}
                </InlineLink>
              </EmptyHint>
            )
          }
        </form.Field>
      </FormSection>
    </Form>
  );
};
